import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Alert, Snackbar, type AlertColor } from '@mui/material';

interface Toast {
  key: number;
  message: string;
  variant: AlertColor;
}

interface NotificationContextValue {
  enqueue: (message: string, variant?: AlertColor) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

let counter = 0;

/** Lightweight snackbar provider — no external dependency, MUI-version agnostic. */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const enqueue = useCallback((message: string, variant: AlertColor = 'info') => {
    counter += 1;
    const key = counter;
    setToasts((t) => [...t, { key, message, variant }]);
  }, []);

  const remove = (key: number) => setToasts((t) => t.filter((x) => x.key !== key));

  return (
    <NotificationContext.Provider value={{ enqueue }}>
      {children}
      {toasts.map((t, i) => (
        <Snackbar
          key={t.key}
          open
          autoHideDuration={4000}
          onClose={() => remove(t.key)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: `${i * 64}px` }}
        >
          <Alert
            severity={t.variant}
            variant="filled"
            onClose={() => remove(t.key)}
            sx={{ boxShadow: 4, borderRadius: 2, minWidth: 280, maxWidth: 420, whiteSpace: 'pre-line' }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationContext() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotificationContext must be used within NotificationProvider');
  return ctx;
}
